/* eslint-disable formatjs/no-literal-string-in-jsx */
/* eslint-disable complexity */
import React from 'react';
import headingSchema from '../../../lib/composer/schemas/heading';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import textSchema from '../../../lib/composer/schemas/text';
import { ctaSchema, ctasSchema } from '../../../lib/composer/schemas/cta';
import themeSchema, { getTextColor } from '../../../lib/composer/schemas/theme';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import Subheading from '../block-ui/Subheading';
import Heading from '../block-ui/Heading';
import CTAs from '../block-ui/CTAs';
import RichText from '../block-ui/RichText';
import EditablePropsType, {
  ComposerBlockElement,
  QuoteBlockProps,
} from '../../../types/composer';
import BlockWrapper from '../BlockWrapper';
import mediaSchema from '../../../lib/composer/schemas/media';
import Media from '../block-ui/Media';
import { transparentize } from '@osmosis/shared/styles/colorModifiers';
import { H3 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';

/**
 * Text banner block.
 */
const Quote: ComposerBlockElement = (props: QuoteBlockProps) => {
  const {
    heading,
    subheading,
    text,
    cta: ctaProps,
    ctas: ctasProps,
    theme = 'bg-0',
    media,
    student,
    topHeader,
  } = props;

  const colors = useColors();

  const bg = colors[theme];
  const ctas = [...(ctaProps?.caption ? [ctaProps] : []), ...(ctasProps || [])];

  const textColor = getTextColor(theme);

  return (
    <Box bg={bg}>
      <BlockWrapper>
        <Box p={3} maxWidth={1000} m="auto">
          <Flex
            flexDirection={['column', 'row']}
            alignItems={['center', 'flex-start']}
          >
            {media && (
              <Box width={300}>
                <Media media={media} />
              </Box>
            )}

            <Flex flex={1} flexDirection="column" sx={{ gap: 3 }}>
              {!!heading && (
                <Heading text={heading} theme={theme} topHeader={topHeader} />
              )}
              {!!subheading && (
                <Subheading
                  text={subheading}
                  theme={theme}
                  topHeader={topHeader}
                />
              )}
              {!!text && (
                <Flex sx={{ position: 'relative', maxWidth: 460 }}>
                  <H3
                    fontSize={76}
                    fontFamily="Tannerscript"
                    sx={{
                      position: 'absolute',
                      bottom: '-60px',
                      right: '-10px',
                      opacity: 0.5,
                    }}
                  >
                    &quot;
                  </H3>
                  <H3
                    fontSize={76}
                    fontFamily="Tannerscript"
                    sx={{
                      position: 'absolute',
                      top: '-60px',
                      left: '-15px',
                      transform: 'rotate(180deg)',
                      opacity: 0.5,
                    }}
                  >
                    &quot;
                  </H3>
                  <RichText
                    html={text}
                    theme={theme}
                    containerProps={{
                      sx: {
                        p: 3,
                        bg: transparentize(0.92, colors[textColor]),
                        lineHeight: 1.5,
                        borderRadius: 5,
                        maxWidth: 460,
                        textAlign: 'unset',
                      },
                    }}
                  />
                </Flex>
              )}

              <Flex>
                {student?.image && (
                  <Box
                    sx={{
                      img: {
                        width: 50,
                        borderRadius: '100%',
                        height: 50,
                        objectFit: 'cover',
                        marginRight: 20,
                      },
                    }}
                  >
                    <img alt="student" src={student.image} />
                  </Box>
                )}
                <Box maxWidth={300}>
                  <H3>{student?.name}</H3>
                  {student?.school && (
                    <P dangerouslySetInnerHTML={{ __html: student?.school }} />
                  )}
                </Box>
              </Flex>

              <CTAs ctas={ctas} theme={theme} />
            </Flex>
          </Flex>
        </Box>
      </BlockWrapper>
    </Box>
  );
};

Quote.schema = {
  name: 'quote',
  label: 'Quote',
  editableProps: [
    {
      name: 'student',
      label: 'Student',
      type: EditablePropsType.Object,
      properties: {
        name: {
          type: EditablePropsType.Text,
          name: 'name',
          label: 'Name',
        },
        school: {
          type: EditablePropsType.Text,
          name: 'school',
          label: 'School',
        },
        image: {
          type: EditablePropsType.Text,
          name: 'image',
          label: 'Image',
        },
      },
    },
    mediaSchema,
    headingSchema,
    subheadingSchema,
    textSchema,
    ctaSchema,
    ctasSchema,
    themeSchema,
  ],
};

export default Quote;
